import { jsx as _jsx } from "react/jsx-runtime";
import { Flow } from 'signup-constants/Flow';
import { isFlow } from '../utils/isFlow';
import { isntExistingUserExistingAccount } from '../app/verificationTypeSelectors';
import { areTherePostAccountCreationSteps, onlyMobileAppPromptStep } from '../flow-management/stepNumberingSelectors';
import { EndpointKey } from '../http/EndpointKey';
import { getHasRefreshedNewPortalAuth } from '../store/app/selectors';
import { arePortalExperimentsFetched } from '../store/experiments/selectors';
import { hasPostExistingAccountSelectionQuestions, isHSFXFLow } from '../store/flow/selectors';
import { isEndpointStatusSuccess } from '../store/http-responses/selectors';
import { isCopyCarouselFinished } from '../store/loading-status/selectors';
import { getIsMobile } from '../utils/deviceUtils';
import BaseLoadingView from './BaseLoadingView';
import IllustrationLoadingView from './IllustrationLoadingView';
import { LoadingKey } from './LoadingKey';
import { getSignupRequestLoadingImageConfig } from './loadingUtils';

const baseLoadingView = ({
  loadingKey,
  hasSpinner,
  imageUrl,
  imageClass,
  textDisplayDelay,
  isIllustration,
  hasConfetti,
  illustrationHeight
}) => () => /*#__PURE__*/_jsx(BaseLoadingView, {
  hasConfetti: hasConfetti,
  hasSpinner: hasSpinner,
  illustrationHeight: illustrationHeight,
  imageClass: imageClass,
  imageUrl: imageUrl,
  isIllustration: isIllustration,
  loadingKey: loadingKey,
  textDisplayDelay: textDisplayDelay
});

const illustrationLoadingView = ({
  loadingKey,
  hasSpinner = false,
  illustration,
  imageClass = '',
  textDisplayDelay = 0,
  hasConfetti
}) => () => /*#__PURE__*/_jsx(IllustrationLoadingView, {
  hasConfetti: hasConfetti,
  hasSpinner: hasSpinner,
  illustration: illustration,
  imageClass: imageClass,
  loadingKey: loadingKey,
  textDisplayDelay: textDisplayDelay
});

const simpleLoadingScreen = ({
  loadingKey,
  imageUrl = '',
  hasSpinner = true,
  shouldClearLoading,
  imageClass = '',
  canSwitchCopy = () => true,
  shouldNotOverrideOtherLoading = () => false,
  textDisplayDelay = 0,
  shouldShowProgressBar = () => false,
  isIllustration = false,
  hasConfetti = false,
  illustrationHeight
}) => ({
  [loadingKey]: {
    view: baseLoadingView({
      loadingKey,
      imageUrl,
      hasSpinner,
      imageClass,
      textDisplayDelay,
      isIllustration,
      hasConfetti,
      illustrationHeight
    }),
    shouldClearLoading,
    canSwitchCopy,
    shouldNotOverrideOtherLoading,
    shouldShowProgressBar
  }
});

const illustrationLoading = ({
  loadingKey,
  hasSpinner,
  shouldClearLoading,
  imageClass,
  shouldNotOverrideOtherLoading = () => false,
  textDisplayDelay = 0,
  illustration,
  shouldShowProgressBar = () => false,
  hasConfetti = false
}) => ({
  [loadingKey]: {
    view: illustrationLoadingView({
      loadingKey,
      illustration,
      hasSpinner,
      imageClass,
      textDisplayDelay,
      hasConfetti
    }),
    shouldClearLoading,
    shouldNotOverrideOtherLoading,
    shouldShowProgressBar
  }
});

const makeSimpleLoadingScreens = () => {
  return Object.keys(LoadingKey).reduce((obj, loadingKey) => Object.assign({}, obj, {}, simpleLoadingScreen({
    loadingKey: loadingKey
  })), {});
};

export const LoadingScreen = Object.assign({}, makeSimpleLoadingScreens(), {}, simpleLoadingScreen({
  loadingKey: LoadingKey.LoginAfterSignup,
  hasSpinner: true,
  textDisplayDelay: 1500,
  shouldNotOverrideOtherLoading: state => !areTherePostAccountCreationSteps(state),
  shouldClearLoading: () => getIsMobile(),
  canSwitchCopy: state => {
    return getHasRefreshedNewPortalAuth(state);
  }
}), {}, illustrationLoading({
  loadingKey: LoadingKey.PartnerExistingAccount,
  illustration: 'bolt',
  shouldClearLoading: state => {
    return isFlow(Flow.DirectoryListing) && isEndpointStatusSuccess(EndpointKey.PartnerLookup)(state);
  }
}), {}, simpleLoadingScreen(Object.assign({
  loadingKey: LoadingKey.GoogleContactsSyncLoading
}, getSignupRequestLoadingImageConfig(), {
  hasSpinner: false,
  hasConfetti: true,
  shouldClearLoading: state => {
    const copyCarouselCheck = isCopyCarouselFinished(state, LoadingKey.GoogleContactsSyncLoading);
    const isSyncActivated = isEndpointStatusSuccess(EndpointKey.GoogleContactsActivateSync)(state);
    return copyCarouselCheck && isSyncActivated;
  }
})), {}, simpleLoadingScreen(Object.assign({
  loadingKey: LoadingKey.SignupRequest
}, getSignupRequestLoadingImageConfig(), {
  hasSpinner: false,
  hasConfetti: true,
  shouldClearLoading: state => {
    if (!areTherePostAccountCreationSteps(state)) {
      return false;
    }

    const copyCarouselCheck = !onlyMobileAppPromptStep(state) ? isCopyCarouselFinished(state, LoadingKey.SignupRequest) : true;
    return isEndpointStatusSuccess(EndpointKey.Signup)(state) && copyCarouselCheck && (arePortalExperimentsFetched(state) || onlyMobileAppPromptStep(state));
  },
  canSwitchCopy: state => {
    return isEndpointStatusSuccess(EndpointKey.Signup)(state) && areTherePostAccountCreationSteps(state) && !onlyMobileAppPromptStep(state);
  },
  shouldShowProgressBar: isHSFXFLow
})), {}, simpleLoadingScreen({
  loadingKey: LoadingKey.PostSignupLoading,
  hasSpinner: true,
  shouldNotOverrideOtherLoading: state => !hasPostExistingAccountSelectionQuestions(state) || isntExistingUserExistingAccount(state),
  shouldClearLoading: state => !hasPostExistingAccountSelectionQuestions(state),
  shouldShowProgressBar: isHSFXFLow
}));
export const getLoading = loadingKey => {
  return LoadingScreen[loadingKey];
};